import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from 'app/store';

/**
 * Creates a memoized selector factory for accessing nested state by a key parameter.
 * This prevents creating new selector functions on every render, which causes
 * unnecessary re-renders and Redux selector memoization warnings.
 *
 * @param stateSelector - Base selector to get the parent state object
 * @returns A function that returns a memoized selector for the given key
 *
 * @example
 * // Instead of:
 * export const selectItemById = (id: string) => (state: RootState) => state.items.byId[id];
 *
 * // Use:
 * const selectItemsById = (state: RootState) => state.items.byId;
 * export const selectItemById = createMemoizedKeySelector(selectItemsById);
 *
 * // In component:
 * const item = useAppSelector(selectItemById('item-123'));
 */
export function createMemoizedKeySelector<T>(
    stateSelector: (state: RootState) => Record<string, T>
): (key: string) => (state: RootState) => T {
    // Cache to store created selectors by key
    const selectorCache = new Map<string, (state: RootState) => T>();

    return (key: string) => {
        if (!selectorCache.has(key)) {
            const memoizedSelector = createSelector(
                [stateSelector],
                (items) => items[key]
            );
            selectorCache.set(key, memoizedSelector);
        }
        return selectorCache.get(key)!;
    };
}

